import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
  useEditorState,
  type Editor,
  type ReactNodeViewProps,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Redo2,
  Undo2,
} from 'lucide-react';

export type PhotoAlign = 'left' | 'center' | 'right' | 'wide';

export interface EditorPhoto {
  uuid: string;
  thumb_url: string;
  title: string;
}

/** Drag payload for a photo dragged in from the About collection strip. */
export const ABOUT_PHOTO_DRAG = 'application/x-about-photo';

/** The About collection's photos, keyed by uuid. Placed photos missing from it are flagged. */
export const AboutPhotosContext = createContext<Map<string, EditorPhoto>>(new Map());

const ALIGNS: { id: PhotoAlign; label: string; title: string }[] = [
  { id: 'left', label: '◧', title: 'Left, text wraps on the right' },
  { id: 'center', label: '▣', title: 'Centered' },
  { id: 'right', label: '◨', title: 'Right, text wraps on the left' },
  { id: 'wide', label: '▭', title: 'Full width' },
];

// ──────────────────────────────────────────────────────────────────────────────
// Photo block: stored as <figure data-photo data-align></figure>, drawn as the
// thumbnail plus align/remove controls. Drag it anywhere to move it.
// ──────────────────────────────────────────────────────────────────────────────

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aboutPhoto: {
      insertAboutPhoto: (uuid: string, pos?: number) => ReturnType;
    };
  }
}

function PhotoView({ node, updateAttributes, deleteNode, selected }: ReactNodeViewProps) {
  const photos = useContext(AboutPhotosContext);
  const { uuid, align } = node.attrs as { uuid: string; align: PhotoAlign };
  const p = photos.get(uuid);
  return (
    <NodeViewWrapper
      className={`about-figure${selected ? ' is-selected' : ''}`}
      data-align={align}
      data-drag-handle
    >
      {p ? (
        <img src={p.thumb_url} alt={p.title} draggable={false} />
      ) : (
        <div className="about-figure-missing-img" />
      )}
      <div className="about-figure-bar" contentEditable={false}>
        {ALIGNS.map((a) => (
          <button
            key={a.id}
            type="button"
            title={a.title}
            className={align === a.id ? 'on' : ''}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => updateAttributes({ align: a.id })}
          >
            {a.label}
          </button>
        ))}
        <button type="button" title="Remove from the page" onMouseDown={(e) => e.preventDefault()} onClick={deleteNode}>
          ×
        </button>
      </div>
      {!p && <div className="about-figure-warning">Not in the About collection — hidden on the site</div>}
    </NodeViewWrapper>
  );
}

const AboutPhoto = Node.create({
  name: 'aboutPhoto',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      uuid: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-photo'),
        renderHTML: (attrs) => ({ 'data-photo': attrs.uuid }),
      },
      align: {
        default: 'center',
        parseHTML: (el) => {
          const a = el.getAttribute('data-align');
          return ALIGNS.some((x) => x.id === a) ? a : 'center';
        },
        renderHTML: (attrs) => ({ 'data-align': attrs.align }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'figure[data-photo]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['figure', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PhotoView);
  },

  addCommands() {
    return {
      insertAboutPhoto:
        (uuid, pos) =>
        ({ commands }) => {
          const content = { type: this.name, attrs: { uuid, align: 'center' } };
          return pos === undefined ? commands.insertContent(content) : commands.insertContentAt(pos, content);
        },
    };
  },
});

// ──────────────────────────────────────────────────────────────────────────────
// Editor
// ──────────────────────────────────────────────────────────────────────────────

/** Matches what the API keeps: web, email, or a path on this site. */
export function normalizeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;
  if (/^(https?:\/\/|mailto:)\S+$/i.test(href) || /^\/(?!\/)\S*$/.test(href)) return href;
  if (/^[^\s@/]+@[^\s@]+\.[^\s@]+$/.test(href)) return `mailto:${href}`;
  if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(href)) return `https://${href}`;
  return null;
}

interface UseAboutEditorOptions {
  /** Initial document; later loads go through `loadContent`. */
  content: string;
  /** Called for edits only (not for loads). */
  onEdit: (editor: Editor) => void;
  /** Cmd/Ctrl+K. */
  onLinkShortcut: () => void;
}

export function useAboutEditor({ content, onEdit, onLinkShortcut }: UseAboutEditorOptions) {
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;
  const onLinkRef = useRef(onLinkShortcut);
  onLinkRef.current = onLinkShortcut;

  return useEditor({
    content,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        strike: false,
        underline: false,
        dropcursor: { color: '#2563eb', width: 3 },
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          defaultProtocol: 'https',
          isAllowedUri: (url) => normalizeHref(url) !== null,
        },
      }),
      Placeholder.configure({ placeholder: 'Tell visitors about the work and the person behind it…' }),
      CharacterCount,
      AboutPhoto,
    ],
    editorProps: {
      attributes: { class: 'about-doc', spellcheck: 'true' },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          onLinkRef.current();
          return true;
        }
        return false;
      },
      // A photo dragged from the collection strip lands where it's dropped
      // (ProseMirror's drop cursor shows the spot). Moving a placed photo is
      // ProseMirror's own drag, untouched here.
      handleDrop: (view, event) => {
        const uuid = event.dataTransfer?.getData(ABOUT_PHOTO_DRAG);
        if (!uuid) return false;
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
        if (!at) return false;
        event.preventDefault();
        const node = view.state.schema.nodes.aboutPhoto.create({ uuid, align: 'center' });
        // Lands between blocks: dropping mid-paragraph would otherwise split it.
        const $pos = view.state.doc.resolve(at.pos);
        const pos = $pos.depth > 0 ? (at.pos - $pos.start(1) > ($pos.end(1) - $pos.start(1)) / 2 ? $pos.after(1) : $pos.before(1)) : at.pos;
        view.dispatch(view.state.tr.insert(pos, node).scrollIntoView());
        return true;
      },
    },
    onUpdate: ({ editor, transaction }) => {
      if (transaction.getMeta('aboutLoad')) return;
      onEditRef.current(editor);
    },
    immediatelyRender: true,
  });
}

/** Replaces the document without an undo step or an edit notification. */
export function loadContent(editor: Editor, html: string) {
  editor.chain().setMeta('addToHistory', false).setMeta('aboutLoad', true).setContent(html, { emitUpdate: false }).run();
}

export function AboutEditorContent({ editor }: { editor: Editor }) {
  return (
    <>
      <style>{EDITOR_CSS}</style>
      <EditorContent editor={editor} />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Toolbar
// ──────────────────────────────────────────────────────────────────────────────

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl+';

export function AboutToolbar({
  editor,
  linkOpen,
  setLinkOpen,
}: {
  editor: Editor;
  linkOpen: boolean;
  setLinkOpen: (open: boolean) => void;
}) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      link: e.isActive('link'),
      href: (e.getAttributes('link').href as string | undefined) ?? '',
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      paragraph: e.isActive('paragraph'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
      words: e.storage.characterCount.words() as number,
    }),
  });

  const chain = () => editor.chain().focus();

  const tool = (
    icon: React.ReactNode,
    title: string,
    on: boolean,
    run: () => void,
    disabled = false
  ) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={on}
      disabled={disabled}
      // Keep the selection in the document while clicking the toolbar.
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
      style={{
        width: 32,
        height: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: 6,
        background: on ? '#1a1a1a' : 'transparent',
        color: on ? '#fff' : disabled ? '#d1d5db' : '#374151',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {icon}
    </button>
  );
  const sep = <span style={{ width: 1, alignSelf: 'stretch', background: '#e5e5e5', margin: '4px 4px' }} />;
  const ic = { size: 16, strokeWidth: 2 };

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        padding: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {tool(<Undo2 {...ic} />, `Undo (${MOD}Z)`, false, () => chain().undo().run(), !s.canUndo)}
        {tool(<Redo2 {...ic} />, `Redo (${MOD}Shift+Z)`, false, () => chain().redo().run(), !s.canRedo)}
        {sep}
        {tool(<Pilcrow {...ic} />, 'Paragraph', s.paragraph && !s.bullet && !s.ordered && !s.quote, () => chain().setParagraph().run())}
        {tool(<Heading2 {...ic} />, 'Heading', s.h2, () => chain().toggleHeading({ level: 2 }).run())}
        {tool(<Heading3 {...ic} />, 'Subheading', s.h3, () => chain().toggleHeading({ level: 3 }).run())}
        {sep}
        {tool(<Bold {...ic} />, `Bold (${MOD}B)`, s.bold, () => chain().toggleBold().run())}
        {tool(<Italic {...ic} />, `Italic (${MOD}I)`, s.italic, () => chain().toggleItalic().run())}
        {tool(<Link2 {...ic} />, `Link (${MOD}K)`, s.link || linkOpen, () => setLinkOpen(!linkOpen))}
        {sep}
        {tool(<List {...ic} />, 'Bulleted list', s.bullet, () => chain().toggleBulletList().run())}
        {tool(<ListOrdered {...ic} />, 'Numbered list', s.ordered, () => chain().toggleOrderedList().run())}
        {tool(<Quote {...ic} />, 'Quote', s.quote, () => chain().toggleBlockquote().run())}
        {tool(<Minus {...ic} />, 'Divider', false, () => chain().setHorizontalRule().run())}
        <span style={{ marginLeft: 'auto', padding: '0 8px', fontSize: 12, color: '#9ca3af' }}>
          {s.words} {s.words === 1 ? 'word' : 'words'}
        </span>
      </div>
      {linkOpen && <LinkBar editor={editor} href={s.href} onClose={() => setLinkOpen(false)} />}
    </div>
  );
}

function LinkBar({ editor, href, onClose }: { editor: Editor; href: string; onClose: () => void }) {
  const [value, setValue] = useState(href);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  const apply = () => {
    if (!value.trim()) return remove();
    const url = normalizeHref(value);
    if (!url) return setError('Use a web address, an email, or a path like /collections');
    const { empty } = editor.state.selection;
    const c = editor.chain().focus().extendMarkRange('link');
    // With nothing selected, insert the address itself as the link text.
    if (empty && !editor.isActive('link')) {
      c.insertContent({ type: 'text', text: url.replace(/^mailto:/, ''), marks: [{ type: 'link', attrs: { href: url } }] }).run();
    } else {
      c.setLink({ href: url }).run();
    }
    onClose();
  };
  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 4px 2px', borderTop: '1px solid #f3f4f6', marginTop: 4 }}>
      <input
        ref={input}
        value={value}
        placeholder="https://…, name@example.com, or /collections"
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
          if (e.key === 'Escape') {
            onClose();
            editor.commands.focus();
          }
        }}
        style={{ flex: 1, padding: '6px 8px', fontSize: 13, border: '1px solid #e5e5e5', borderRadius: 6 }}
      />
      <button type="button" onClick={apply} style={linkButton}>
        Apply
      </button>
      {href && (
        <button type="button" onClick={remove} style={{ ...linkButton, color: '#991b1b' }}>
          Remove
        </button>
      )}
      {error && <span style={{ fontSize: 12, color: '#991b1b' }}>{error}</span>}
    </div>
  );
}

const linkButton: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 13,
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
};

// Mirrors the storefront's About page typography so the editor reads as the page will.
const EDITOR_CSS = `
.about-doc {
  min-height: 560px;
  padding: 36px 48px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 0 0 8px 8px;
  outline: none;
  font-size: 17px;
  line-height: 1.75;
  color: #374151;
}
.about-doc::after { content: ''; display: block; clear: both; }
.about-doc > * + * { margin-top: 0; }
.about-doc p { margin: 0 0 1.25em; }
.about-doc h2 { font-family: Georgia, serif; font-weight: 400; font-size: 28px; line-height: 1.3; color: #111; margin: 1.5em 0 0.6em; }
.about-doc h3 { font-family: Georgia, serif; font-weight: 400; font-size: 21px; line-height: 1.4; color: #111; margin: 1.3em 0 0.5em; }
.about-doc > :first-child { margin-top: 0; }
.about-doc strong { font-weight: 600; color: #111; }
.about-doc a { color: #111; text-decoration: underline; text-underline-offset: 3px; }
.about-doc ul, .about-doc ol { margin: 0 0 1.25em; padding-left: 1.5em; }
.about-doc ul { list-style: disc; }
.about-doc ol { list-style: decimal; }
.about-doc li > p { margin: 0 0 0.35em; }
.about-doc blockquote { margin: 0 0 1.25em; padding-left: 1.25em; border-left: 2px solid #d1d5db; font-family: Georgia, serif; font-style: italic; font-size: 19px; color: #4b5563; }
.about-doc blockquote p { margin-bottom: 0.5em; }
.about-doc hr { border: none; border-top: 1px solid #e5e5e5; margin: 2.5em auto; width: 40%; clear: both; }
.about-doc hr.ProseMirror-selectednode { border-top-color: #2563eb; }
.about-doc p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #9ca3af; float: left; height: 0; pointer-events: none; }
.about-figure { position: relative; margin: 0.5em auto 1.5em; cursor: grab; user-select: none; }
/* Centered and full-width photos sit below any floated one rather than under it. */
.about-figure[data-align="center"] { width: 70%; clear: both; }
.about-figure[data-align="wide"] { width: 100%; clear: both; }
.about-figure[data-align="left"] { float: left; width: 45%; margin: 0.35em 2em 1em 0; }
.about-figure[data-align="right"] { float: right; width: 45%; margin: 0.35em 0 1em 2em; }
.about-figure.is-selected img, .about-figure.is-selected .about-figure-missing-img { outline: 3px solid #2563eb; outline-offset: 2px; }
.about-figure img { display: block; width: 100%; border-radius: 2px; }
.about-figure-missing-img { aspect-ratio: 3 / 2; background: repeating-linear-gradient(45deg, #f3f4f6 0 10px, #e5e7eb 10px 20px); }
.about-figure-warning { position: absolute; left: 8px; right: 8px; bottom: 8px; padding: 4px 8px; border-radius: 4px; background: #fef3c7; color: #92400e; font-size: 12px; line-height: 1.4; }
.about-figure-bar { position: absolute; top: 8px; right: 8px; display: flex; gap: 2px; padding: 3px; border-radius: 6px; background: rgba(17,17,17,0.75); opacity: 0; transition: opacity 0.15s; }
.about-figure:hover .about-figure-bar, .about-figure.is-selected .about-figure-bar { opacity: 1; }
.about-figure-bar button { width: 26px; height: 24px; border: 0; border-radius: 4px; background: transparent; color: #fff; font-size: 14px; line-height: 1; cursor: pointer; }
.about-figure-bar button:hover { background: rgba(255,255,255,0.15); }
.about-figure-bar button.on { background: #fff; color: #111; }
`;
